import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Wand2, Download, Loader2, ImageIcon, 
  Sparkles, ChevronDown, Trash2, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLE_PRESETS = [
  { label: 'Photorealistic', prefix: 'photorealistic, hyperdetailed, 8k, ' },
  { label: 'Anime', prefix: 'anime style, detailed illustration, ' },
  { label: 'Digital Art', prefix: 'digital art, detailed, vibrant colors, ' },
  { label: 'Oil Painting', prefix: 'oil painting style, classical, detailed brushwork, ' },
  { label: 'Fantasy', prefix: 'fantasy art, magical, ethereal lighting, ' },
  { label: 'Cinematic', prefix: 'cinematic still, dramatic lighting, film grain, ' },
];

const SIZE_OPTIONS = [
  { label: 'Square', width: 768, height: 768, icon: '1:1' },
  { label: 'Portrait', width: 768, height: 1024, icon: '3:4' },
  { label: 'Landscape', width: 1024, height: 768, icon: '4:3' },
  { label: 'Wide', width: 1280, height: 768, icon: '16:9' },
];

const AIImageGenerator = () => {
  const { isAuthenticated, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedSize, setSelectedSize] = useState(SIZE_OPTIONS[0]);
  const [numImages, setNumImages] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchGallery();
    }
  }, [isAuthenticated]);

  const fetchGallery = async () => {
    try {
      const res = await fetch(`${API}/ai/gallery`, getAuthHeader());
      const data = await res.json();
      setGallery(data.images || []);
    } catch (err) {
      console.error('Failed to fetch gallery');
    }
  };

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to use the AI Image Generator');
      navigate('/login');
      return;
    }

    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setGenerating(true);
    setGeneratedImages([]);

    try {
      const fullPrompt = selectedStyle 
        ? `${selectedStyle.prefix}${prompt}` 
        : prompt;

      const res = await fetch(`${API}/ai/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader().headers
        },
        body: JSON.stringify({
          prompt: fullPrompt,
          width: selectedSize.width,
          height: selectedSize.height,
          num_images: numImages
        })
      });

      const data = await res.json();
      
      if (data.success && data.images?.length) {
        setGeneratedImages(data.images);
        fetchGallery();
        toast.success(`Generated ${data.images.length} image(s)!`);
      } else {
        toast.error(data.detail || 'Generation failed');
      }
    } catch (err) {
      toast.error('Failed to generate image. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (imageUrl, index) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `velvetstream-ai-${Date.now()}-${index}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#050505]" data-testid="ai-generator-page">
      <SEO 
        title="AI Image Generator" 
        description="Generate stunning AI images with VelvetStream's NSFW AI Image Generator. Create custom artwork from text prompts."
        path="/ai-generator"
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">AI Powered</span>
          </div>
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="ai-generator-title"
          >
            AI Image Generator
          </h1>
          <p className="text-gray-400 text-base max-w-2xl mx-auto">
            Create stunning images from text descriptions. No limits, no filters — pure creative freedom.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Controls Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Prompt Input */}
            <div className="bg-[#0E0E0E] border border-white/5 rounded-lg p-5">
              <label className="text-sm text-gray-400 uppercase tracking-wider mb-3 block">
                Describe your image
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="A beautiful woman in a red dress standing in a neon-lit cyberpunk city..."
                className="w-full h-32 bg-[#1A1A1A] border border-white/10 rounded-lg p-4 text-white placeholder-gray-600 resize-none focus:outline-none focus:border-purple-500/50 transition-colors"
                data-testid="ai-prompt-input"
              />
            </div>

            {/* Style Presets */}
            <div className="bg-[#0E0E0E] border border-white/5 rounded-lg p-5">
              <label className="text-sm text-gray-400 uppercase tracking-wider mb-3 block">
                Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                {STYLE_PRESETS.map((style) => (
                  <button
                    key={style.label}
                    onClick={() => setSelectedStyle(selectedStyle?.label === style.label ? null : style)}
                    className={`px-3 py-2 rounded-md text-sm transition-all ${
                      selectedStyle?.label === style.label
                        ? 'bg-purple-600 text-white'
                        : 'bg-[#1A1A1A] text-gray-400 hover:bg-white/5 border border-white/5'
                    }`}
                    data-testid={`style-${style.label.toLowerCase()}`}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Size Options */}
            <div className="bg-[#0E0E0E] border border-white/5 rounded-lg p-5">
              <label className="text-sm text-gray-400 uppercase tracking-wider mb-3 block">
                Size
              </label>
              <div className="grid grid-cols-2 gap-2">
                {SIZE_OPTIONS.map((size) => (
                  <button
                    key={size.label}
                    onClick={() => setSelectedSize(size)}
                    className={`px-3 py-2 rounded-md text-sm transition-all flex items-center justify-center gap-2 ${
                      selectedSize.label === size.label
                        ? 'bg-purple-600 text-white'
                        : 'bg-[#1A1A1A] text-gray-400 hover:bg-white/5 border border-white/5'
                    }`}
                    data-testid={`size-${size.label.toLowerCase()}`}
                  >
                    <span className="text-xs opacity-60">{size.icon}</span>
                    {size.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Number of Images */}
            <div className="bg-[#0E0E0E] border border-white/5 rounded-lg p-5">
              <label className="text-sm text-gray-400 uppercase tracking-wider mb-3 block">
                Number of images
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => setNumImages(n)}
                    className={`flex-1 py-2 rounded-md text-sm transition-all ${
                      numImages === n
                        ? 'bg-purple-600 text-white'
                        : 'bg-[#1A1A1A] text-gray-400 hover:bg-white/5 border border-white/5'
                    }`}
                    data-testid={`num-images-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            {!isAuthenticated ? (
              <Button
                onClick={() => navigate('/login')}
                className="w-full py-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium text-lg rounded-lg"
                data-testid="ai-login-btn"
              >
                <Lock className="w-5 h-5 mr-2" />
                Sign In to Generate
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="w-full py-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium text-lg rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="ai-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {/* Current Generation */}
            {generating && (
              <div className="bg-[#0E0E0E] border border-white/5 rounded-lg p-12 flex flex-col items-center justify-center min-h-[400px]">
                <div className="relative mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 animate-pulse" />
                  <Wand2 className="w-8 h-8 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <p className="text-white text-lg font-medium mb-2">Creating your image...</p>
                <p className="text-gray-500 text-sm">This usually takes 10-20 seconds</p>
              </div>
            )}

            {/* Generated Results */}
            {!generating && generatedImages.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl text-white font-medium">Generated Images</h2>
                <div className={`grid gap-4 ${generatedImages.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                  {generatedImages.map((img, index) => (
                    <div 
                      key={index} 
                      className="relative group bg-[#0E0E0E] border border-white/5 rounded-lg overflow-hidden cursor-pointer"
                      onClick={() => setSelectedImage(img)}
                      data-testid={`generated-image-${index}`}
                    >
                      <img 
                        src={img.url} 
                        alt={`Generated ${index + 1}`}
                        className="w-full h-auto"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(img.url, index); }}
                          className="px-4 py-2 bg-white text-black rounded-lg font-medium flex items-center gap-2 hover:bg-gray-200 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!generating && generatedImages.length === 0 && (
              <div className="bg-[#0E0E0E] border border-white/5 rounded-lg p-12 flex flex-col items-center justify-center min-h-[400px]">
                <ImageIcon className="w-16 h-16 text-gray-700 mb-4" />
                <p className="text-gray-500 text-lg mb-2">Your creations will appear here</p>
                <p className="text-gray-600 text-sm">Enter a prompt and hit Generate</p>
              </div>
            )}

            {/* Gallery Toggle */}
            {gallery.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowGallery(!showGallery)}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
                  data-testid="gallery-toggle"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${showGallery ? 'rotate-180' : ''}`} />
                  Previous Generations ({gallery.length})
                </button>

                {showGallery && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {gallery.map((item, idx) => (
                      <div key={item.id || idx} className="group relative">
                        {item.images?.map((img, imgIdx) => (
                          <div 
                            key={imgIdx}
                            className="bg-[#0E0E0E] border border-white/5 rounded-lg overflow-hidden cursor-pointer mb-2"
                            onClick={() => setSelectedImage(img)}
                          >
                            <img 
                              src={img.url} 
                              alt={item.prompt}
                              className="w-full h-auto"
                            />
                            <div className="p-2">
                              <p className="text-xs text-gray-500 line-clamp-1">{item.prompt}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="image-lightbox"
        >
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedImage.url} 
              alt="Generated" 
              className="max-w-full max-h-[85vh] rounded-lg object-contain"
            />
            <div className="absolute bottom-4 right-4 flex gap-2">
              <button
                onClick={() => handleDownload(selectedImage.url, 0)}
                className="px-4 py-2 bg-white text-black rounded-lg font-medium flex items-center gap-2 hover:bg-gray-200"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={() => setSelectedImage(null)}
                className="px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIImageGenerator;
